import React, { useState, useEffect, useCallback } from 'react';
import {
  FaSave, FaCalendarDay, FaUsers, FaMagic, FaCheckCircle, FaLock,
} from 'react-icons/fa';
import api, { errorMessage } from '../api';
import {
  PageShell, Card, Button, Field, inputClass, Alert, Loading,
  formatDate, todayISO,
} from './attendance/ui';

const STATUS_OPTIONS = [
  ['present', 'Present'], ['late', 'Late'], ['half_day', 'Half day'],
  ['absent', 'Absent'], ['on_leave', 'On leave'],
];

/**
 * Manual daily attendance capture for supervisors and HR — the fallback when
 * a device is down, and the primary route at rural sites.
 */
const AttendanceEntry = () => {
  const [date, setDate] = useState(todayISO());
  const [locationFilter, setLocationFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [staff, setStaff] = useState([]);
  const [options, setOptions] = useState({ locations: [], departments: [] });
  const [rows, setRows] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [closing, setClosing] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    Promise.all([api.get('locations/'), api.get('departments/')])
      .then(([locations, departments]) => setOptions({
        locations: locations.data, departments: departments.data,
      }))
      .catch(() => { /* filters stay empty */ });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (locationFilter) params.location = locationFilter;
      if (departmentFilter) params.department = departmentFilter;

      const [staffRes, attendanceRes] = await Promise.all([
        api.get('employees/', { params }),
        api.get('attendance/', { params: { ...params, start_date: date, end_date: date } }),
      ]);

      const active = staffRes.data.filter((employee) => employee.status === 'active');
      setStaff(active);

      const existing = Object.fromEntries(
        attendanceRes.data.map((record) => [record.employeeId, record]));
      setRows(Object.fromEntries(active.map((employee) => {
        const record = existing[employee.employeeId];
        return [employee.employeeId, {
          status: record?.status || 'present',
          checkIn: record?.checkIn ? new Date(record.checkIn).toTimeString().slice(0, 5) : '',
          checkOut: record?.checkOut ? new Date(record.checkOut).toTimeString().slice(0, 5) : '',
          remarks: record?.remarks || '',
          saved: Boolean(record),
        }];
      })));
    } catch (err) {
      setError(errorMessage(err, 'Could not load the daily sheet.'));
    } finally {
      setLoading(false);
    }
  }, [date, locationFilter, departmentFilter]);

  useEffect(() => { load(); }, [load]);

  const setCell = (employeeId, key, value) =>
    setRows((current) => ({
      ...current,
      [employeeId]: { ...current[employeeId], [key]: value },
    }));

  /** Fill every blank row with the standard working day. */
  const fillDefaults = () => {
    setRows((current) => Object.fromEntries(
      Object.entries(current).map(([id, row]) => [id, row.checkIn ? row : {
        ...row, status: 'present', checkIn: '08:30', checkOut: '17:00',
      }]),
    ));
  };

  const save = async () => {
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const records = Object.entries(rows).map(([employeeId, row]) => ({
        employeeId,
        status: row.status,
        checkIn: ['absent', 'on_leave'].includes(row.status) ? null : row.checkIn || null,
        checkOut: ['absent', 'on_leave'].includes(row.status) ? null : row.checkOut || null,
        remarks: row.remarks || null,
      }));
      const { data } = await api.post('attendance/bulk-entry/', { date, records });
      setNotice(`${data.saved} record(s) saved for ${formatDate(date)}.`
        + (data.errors?.length ? ` ${data.errors.length} row(s) skipped.` : ''));
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Could not save the sheet.'));
    } finally {
      setSaving(false);
    }
  };

  const closeDay = async () => {
    if (!window.confirm(`Close ${formatDate(date)}? Staff with no record are marked absent (or on leave).`)) return;
    setClosing(true);
    setError('');
    try {
      const { data } = await api.post('attendance/mark-absentees/',
        { date, location: locationFilter || undefined });
      setNotice(`${data.created} absence/leave row(s) created for ${formatDate(date)}.`);
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Could not close the day.'));
    } finally {
      setClosing(false);
    }
  };

  const savedCount = Object.values(rows).filter((row) => row.saved).length;

  return (
    <PageShell
      title="Daily Attendance Entry"
      subtitle={`${staff.length} staff · ${savedCount} already recorded for ${formatDate(date)}`}
      actions={
        <>
          <Button variant="secondary" onClick={fillDefaults}><FaMagic /> Fill defaults</Button>
          <Button variant="secondary" onClick={closeDay} disabled={closing}>
            <FaLock /> {closing ? 'Closing…' : 'Close day'}
          </Button>
          <Button onClick={save} disabled={saving || staff.length === 0}>
            <FaSave /> {saving ? 'Saving…' : 'Save sheet'}
          </Button>
        </>
      }
    >
      <Alert>{error}</Alert>
      <Alert tone="success">{notice}</Alert>

      <Card className="mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Field label="Date">
            <input type="date" value={date} onChange={(event) => setDate(event.target.value)}
              className={inputClass} />
          </Field>
          <Field label="Location">
            <select value={locationFilter} className={inputClass}
              onChange={(event) => setLocationFilter(event.target.value)}>
              <option value="">All locations</option>
              {options.locations.map((item) => (
                <option key={item.id} value={item.id}>{item.name} ({item.type})</option>
              ))}
            </select>
          </Field>
          <Field label="Department">
            <select value={departmentFilter} className={inputClass}
              onChange={(event) => setDepartmentFilter(event.target.value)}>
              <option value="">All departments</option>
              {options.departments.map((item) => (
                <option key={item.id} value={item.id}>{item.name}</option>
              ))}
            </select>
          </Field>
        </div>
      </Card>

      <Card title={<span className="flex items-center gap-2"><FaCalendarDay className="text-gray-400" /> Sheet for {formatDate(date)}</span>}>
        {loading ? <Loading /> : staff.length === 0 ? (
          <div className="py-10 text-center text-gray-500">
            <FaUsers className="mx-auto text-3xl text-gray-300 mb-2" />
            No active staff match these filters.
          </div>
        ) : (
          <div className="overflow-x-auto -mx-6 px-6">
            <table className="w-full min-w-[820px] text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  {['Staff ID', 'Name', 'Department', 'Status', 'Check in', 'Check out', 'Remarks', ''].map((column) => (
                    <th key={column} className="py-3 pr-3 font-semibold whitespace-nowrap">{column}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {staff.map((employee) => {
                  const row = rows[employee.employeeId] || {};
                  const noPunch = ['absent', 'on_leave'].includes(row.status);
                  return (
                    <tr key={employee.id} className="border-b last:border-0 hover:bg-gray-50">
                      <td className="py-2 pr-3 font-mono text-xs text-gray-600">{employee.employeeId}</td>
                      <td className="py-2 pr-3 font-medium text-gray-800 whitespace-nowrap">{employee.name}</td>
                      <td className="py-2 pr-3 whitespace-nowrap text-gray-600">{employee.department || '—'}</td>
                      <td className="py-2 pr-3">
                        <select value={row.status || 'present'}
                          onChange={(event) => setCell(employee.employeeId, 'status', event.target.value)}
                          className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-[#15A857]">
                          {STATUS_OPTIONS.map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                          ))}
                        </select>
                      </td>
                      <td className="py-2 pr-3">
                        <input type="time" value={row.checkIn || ''} disabled={noPunch}
                          onChange={(event) => setCell(employee.employeeId, 'checkIn', event.target.value)}
                          className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-[#15A857] disabled:bg-gray-100" />
                      </td>
                      <td className="py-2 pr-3">
                        <input type="time" value={row.checkOut || ''} disabled={noPunch}
                          onChange={(event) => setCell(employee.employeeId, 'checkOut', event.target.value)}
                          className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-[#15A857] disabled:bg-gray-100" />
                      </td>
                      <td className="py-2 pr-3">
                        <input value={row.remarks || ''} placeholder="Optional"
                          onChange={(event) => setCell(employee.employeeId, 'remarks', event.target.value)}
                          className="w-full min-w-[140px] px-2 py-1.5 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-[#15A857]" />
                      </td>
                      <td className="py-2 pr-3">
                        {row.saved && <FaCheckCircle className="text-green-500" title="Already recorded" />}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </PageShell>
  );
};

export default AttendanceEntry;
